/**
 * Parse `**bold**` out of bot LLM output so the MessageBubble can render real
 * emphasis. Used together with `parseInlineRefs` / `parseMarkdownLinks` — call
 * this last, on the plain-text segments those produce.
 *
 * The backend's pre-built listings (`flight_list_message`,
 * `package_list_message`, `room_list_message`) mark up hotel names and prices
 * with CommonMark's double asterisk, and the bubble rendered the raw string —
 * so users saw literal punctuation: "**RIXOS PREMIUM** … from **$450**".
 *
 * Token format: **text**
 *   - text: at least one character, no `*` and no newline
 *
 * Bounding it to a single line is what keeps an unbalanced `**` in free LLM
 * prose from swallowing the rest of the message; such tokens fall through as
 * plain text.
 */
export type MarkdownBoldSegment =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string };

const BOLD_RE = /\*\*([^*\n]+)\*\*/g;

export function parseMarkdownBold(text: string): MarkdownBoldSegment[] {
  if (!text) return [{ type: 'text', value: '' }];

  const out: MarkdownBoldSegment[] = [];
  let cursor = 0;

  for (const match of Array.from(text.matchAll(BOLD_RE))) {
    const [full, rawValue] = match;
    const idx = match.index ?? 0;
    const value = rawValue || '';

    if (!value.trim()) continue;

    if (idx > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, idx) });
    }
    out.push({ type: 'bold', value });
    cursor = idx + full.length;
  }

  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) });
  }
  return out.length ? out : [{ type: 'text', value: text }];
}
